import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "@components/Button";
import ScalableWrapper from "@components/ScalableWrapper";
import { useDebounce } from "@hooks/useDebounce";
import { useManager } from "@src/contexts/WarsContext";
import { images } from "@utils/media";
import { languages } from "@playroomkit/pr-wars-data";
import View from "@components/View";
import { useShop } from "@src/contexts/ShopProvider";
import { SendPlayerAllAccessStatusEvent, WarsEvents } from "@playroomkit/pr-wars/events";

const LanguageSelector = (props: { closePopup: () => void }) => {
  const { closePopup } = props;

  const manager = useManager();
  const { i18n, t } = useTranslation(["main"]);

  const [hasPlayerAllAccess, setHasPlayerAllAccess] = useState(false);

  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);

  const handleChangeLanguage = useDebounce((language: string) => {
    i18n.changeLanguage(language);
    manager.setLanguage(language);
  }, 150);

  useEffect(() => {
    setHasPlayerAllAccess(manager.getMyPlayer()?.allAccess ?? false);
  }, [manager]);

  useEffect(() => {
    const handlePlayerAllAccessStatusEvent = (event: Event) => {
      const e = event as SendPlayerAllAccessStatusEvent;
      setHasPlayerAllAccess(!!e.allAccess);
    }

    document.addEventListener(WarsEvents.SendPlayerAllAccessStatusEvent, handlePlayerAllAccessStatusEvent);

    return () => {
      document.removeEventListener(WarsEvents.SendPlayerAllAccessStatusEvent, handlePlayerAllAccessStatusEvent);
    }
  }, [])

  const changeLanguage = (id: string) => {
    handleChangeLanguage(id);
    setSelectedLanguage(id);
  };

  return (
    <View className="w-screen h-dvh absolute top-0 left-0 z-[999999] font-[Slackey]">
      <View
        enableEventCapturing
        className="w-full h-full absolute top-0 left-0 bg-black/60"
      />
      <ScalableWrapper maintainAspectRatioBy="height">
        <View className="w-full h-full flex items-center justify-center">
          <View className="relative bg-white w-80 px-4 rounded-xl flex flex-col items-center pb-4">
            <Button
              onClick={closePopup}
              className="bg-transparent border-none outline-none absolute -top-4 -right-4"
            >
              <img
                src={images.ClosePopupIcon}
                alt="close-popup"
                className="w-8 h-8 object-contain"
              />
            </Button>
            <View className="text-center pb-2">
              <h1 className="text-black mb-0 text-lg">
                {t("language_selector_title")}
              </h1>
              <p className="font-mono font-bold text-xs my-0">
                {t("language_selector_description")}
              </p>
            </View>
            <View
              enableEventCapturing
              className="w-full h-80 flex flex-col items-start overflow-auto px-2 pb-2 no-scrollbar"
            >
              {languages.map((language) => {
                return (
                  <View
                    key={language.id}
                    className="w-full flex items-center justify-between border-0 border-b-2 border-solid border-gray-500/50"
                  >
                    <p className="my-[0.75rem]">{language.name}</p>
                    {selectedLanguage === language.id ? (
                      <img
                        src={images.BlackTickIcon}
                        className="w-6 h-6 object-contain"
                      />
                    ) : hasPlayerAllAccess ? (
                      <Button
                        onClick={() => changeLanguage(language.id)}
                        className="bg-transparent border-none outline-none font-mono font-bold text-[#FF55D8]"
                      >
                        Try
                      </Button>
                    ) : (
                      <img
                        src={images.LockIcon}
                        className="w-6 h-6 object-contain"
                      />
                    )}
                  </View>
                );
              })}
            </View>
            {!hasPlayerAllAccess && <UnlockWithAllAccessButton />}
          </View>
        </View>
      </ScalableWrapper>
    </View>
  );
};

const UnlockWithAllAccessButton = () => {
  const { handleSubscribe } = useShop();
  const { t } = useTranslation(["main"]);

  return (
    <Button
      onClick={handleSubscribe}
      className="w-full rounded-lg p-1 inline-block relative shadow-[2px_3px_0_0_#000] border-none outline-none bg-[#FF8838] mt-4"
    >
      <div className="font-[Slackey] text-base flex flex-col items-center justify-center gap-0.5 text-black bg-[#FF65EB] py-1.5">
        <h3 className="my-0 text-[0.65rem] leading-none">
          {t("language_selector_unlock_with")}
        </h3>
        <h1 className="my-0 text-lg leading-none">
          {t("language_selector_all_access")}
        </h1>
      </div>
    </Button>
  );
};

export default LanguageSelector;
